import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { Box, Typography } from '@/shared/design-system/primitives';

interface TacticalEmblaWheelProps {
    data: string[];
    value: string;
    onSelect: (val: string) => void;
    label: string;
}

/**
 * TacticalEmblaWheel: World-class grade wheel picker powered by Embla.
 * V6.0: Headless physics with 100% custom tactical branding.
 */
export const TacticalEmblaWheel: React.FC<TacticalEmblaWheelProps> = ({ data, value, onSelect, label }) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({
        axis: 'y',
        loop: true,
        dragFree: true,
        containScroll: false
    });

    const [selectedIndex, setSelectedIndex] = useState(data.indexOf(value));

    const onScroll = useCallback(() => {
        if (!emblaApi) return;
        const index = emblaApi.selectedScrollSnap();
        if (index !== selectedIndex) {
            setSelectedIndex(index);
            onSelect(data[index]);
        }
    }, [emblaApi, selectedIndex, onSelect, data]);

    useEffect(() => {
        if (!emblaApi) return;
        emblaApi.on('select', onScroll);
        emblaApi.on('reInit', onScroll);
        
        // --- MOUSE WHEEL SUPPORT (Optimized for Minimum Sensitivity) ---
        const engine = emblaApi.containerNode();
        let lastWheelTime = 0;
        const handleWheel = (e: WheelEvent) => {
            e.preventDefault();
            const now = Date.now();
            
            // Extreme damping for precision: 800ms cooldown + 50px delta threshold
            if (now - lastWheelTime < 800) return; 
            if (Math.abs(e.deltaY) < 50) return; 
            
            if (e.deltaY > 0) emblaApi.scrollNext();
            else emblaApi.scrollPrev();
            
            lastWheelTime = now;
        };
        engine.addEventListener('wheel', handleWheel, { passive: false });

        // Initial jump to value
        const targetIdx = data.indexOf(value);
        if (targetIdx !== -1) {
            emblaApi.scrollTo(targetIdx, true);
        }

        return () => {
            engine.removeEventListener('wheel', handleWheel);
        };
    }, [emblaApi, onScroll, value, data]);

    return (
        <Box style={{ flex: 1, alignItems: 'center' }}>
            <Typography style={{ fontSize: 7, fontWeight: '900', color: '#444', marginBottom: 8, letterSpacing: 1.5 }}>{label}</Typography>
            
            <div style={{ 
                height: '132px', 
                width: '100%', 
                backgroundColor: '#050505', 
                borderRadius: '8px', 
                border: '1px solid #222', 
                overflow: 'hidden', 
                position: 'relative',
                touchAction: 'none'
            }}>
                {/* THE TACTICAL BOX: Restored selection design */}
                <div style={{ 
                    position: 'absolute', 
                    top: '44px', 
                    left: 4, 
                    right: 4, 
                    height: '44px', 
                    border: '1px solid rgba(242, 155, 17, 0.4)',
                    backgroundColor: 'rgba(242, 155, 17, 0.15)',
                    borderRadius: '4px',
                    pointerEvents: 'none',
                    zIndex: 10
                }} />

                <div className="embla" ref={emblaRef} style={{ height: '100%', cursor: 'grab' }}>
                    <div className="embla__container" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {data.map((item, i) => (
                            <div 
                                className="embla__slide" 
                                key={i} 
                                style={{ 
                                    height: '44px', 
                                    flex: '0 0 44px', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    transition: 'opacity 0.2s, transform 0.2s',
                                    opacity: i === selectedIndex ? 1 : 0.8, 
                                    transform: i === selectedIndex ? 'scale(1.1)' : 'scale(0.9)'
                                }}
                            >
                                <span style={{ 
                                    color: i === selectedIndex ? '#f29b11' : '#eee', 
                                    fontFamily: 'JetBrains Mono, monospace', 
                                    fontSize: '16px', 
                                    fontWeight: 900 
                                }}>
                                    {item}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </Box>
    );
};
